type AnalyticsSource =
  | 'direct'
  | 'google'
  | 'bing'
  | 'facebook'
  | 'ai'
  | 'other';

const ENDPOINT = 'https://receipt.bucksimple.com/api/analytics';
const SOURCE_PARAMETER = 'bs_source';
const SOURCE_STORAGE_KEY = 'bucksimple-acquisition-source';
const SOURCES = new Set<AnalyticsSource>([
  'direct',
  'google',
  'bing',
  'facebook',
  'ai',
  'other',
]);

function matches(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

function isGoogle(hostname: string): boolean {
  const base = hostname.replace(/^(?:www|news|maps|accounts|m)\./, '');
  return /^google\.(?:com|[a-z]{2,3}|co\.[a-z]{2}|com\.[a-z]{2})$/.test(base);
}

function sourceFromReferrer(): AnalyticsSource {
  if (!document.referrer) return 'direct';
  try {
    const hostname = new URL(document.referrer).hostname.toLowerCase();
    if (
      matches(hostname, 'bucksimple.com') ||
      hostname.endsWith('.rob243486.chatgpt.site')
    )
      return 'direct';
    if (
      matches(hostname, 'chatgpt.com') ||
      matches(hostname, 'perplexity.ai') ||
      matches(hostname, 'claude.ai') ||
      matches(hostname, 'copilot.microsoft.com') ||
      matches(hostname, 'gemini.google.com') ||
      matches(hostname, 'meta.ai') ||
      matches(hostname, 'you.com')
    )
      return 'ai';
    if (
      isGoogle(hostname) ||
      matches(hostname, 'googleadservices.com')
    )
      return 'google';
    if (matches(hostname, 'bing.com')) return 'bing';
    if (
      matches(hostname, 'facebook.com') ||
      matches(hostname, 'fb.com') ||
      matches(hostname, 'messenger.com')
    )
      return 'facebook';
    return 'other';
  } catch {
    return 'other';
  }
}

function acquisitionSource(): AnalyticsSource {
  try {
    const url = new URL(location.href);
    const parameter = url.searchParams.get(SOURCE_PARAMETER);
    if (parameter && SOURCES.has(parameter as AnalyticsSource)) {
      const source = parameter as AnalyticsSource;
      sessionStorage.setItem(SOURCE_STORAGE_KEY, source);
      url.searchParams.delete(SOURCE_PARAMETER);
      history.replaceState(
        history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`,
      );
      return source;
    }
    const stored = sessionStorage.getItem(SOURCE_STORAGE_KEY);
    if (stored && SOURCES.has(stored as AnalyticsSource))
      return stored as AnalyticsSource;
  } catch {
    // Storage and history are optional; referrer classification still works.
  }
  return sourceFromReferrer();
}

function collectionAllowed(): boolean {
  const browser = navigator as Navigator & { globalPrivacyControl?: boolean };
  if (browser.webdriver || browser.globalPrivacyControl === true) return false;
  const doNotTrack = String(
    browser.doNotTrack ??
      (globalThis as typeof globalThis & { doNotTrack?: string }).doNotTrack ??
      '',
  ).toLowerCase();
  return doNotTrack !== '1' && doNotTrack !== 'yes';
}

let pageViewSent = false;

export function trackPageView(target: string): void {
  if (pageViewSent || !collectionAllowed()) return;
  pageViewSent = true;
  void fetch(ENDPOINT, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    keepalive: true,
    referrerPolicy: 'no-referrer',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({
      event: 'page_view',
      target,
      source: acquisitionSource(),
    }),
  }).catch(() => undefined);
}
